/*
 * user Sagas
 *
 * @version 1.0.0
 * @author Stefano Servodidio
 * @since 05-Aug-2024
 */

import React from 'react'
import { put, call, all, select, takeLatest, delay, take } from 'redux-saga/effects'
import { doLocalLoading } from 'Store/actions/loading'
import PortalMessage from 'Hoc/PortalMessage'
import { getUserTenantId, getUserMerchantId, getUserRoles, isSSOUser } from 'Store/selectors'
import { userRolesString, deleteEmptyValues, logoutRedirect } from 'Utility/globalMethods'
import { findPopupData } from 'Utility/subscriptionMethods'
import { doPopup } from 'Store/actions/popup'
import { getBankFromPath } from 'Utility/bankMethods'
import * as actionsLang from 'Store/actions/language'
import * as actions from '../actions/user'
import * as actionTerminal from 'Route/TerminalsManagement/modules/TerminalsManagementActions'
import * as api from 'Api/user/user'
import vasFormattedMessage from 'Route/TerminalsManagement/components/Vas/languageModule'
import formattedMessage from 'Route/TerminalsManagement/components/TerminalConfiguration/languageModule'


const popupSuccessResult = {
  title: formattedMessage.successMessage,
  isSuccess: true
}

const popupErrorResult = {
  title: formattedMessage.errorTitle,
  description: formattedMessage.errorMessage,
  isError: true
}

export const messages = {
  themePopupTitle:  <PortalMessage id='t.app.project.popup.default.error.title' defaultMessage='Ops!' />,
  themePopupSubtitle: <PortalMessage id='t.app.project.popup.theme.subtitle.failure' defaultMessage='Your user is not configured properly. Please contact support or your administrator for assistance.' />,
  themeExitButton: <PortalMessage id='t.app.project.exit' defaultMessage='Exit' />
}

export function* getTerminalIdListSaga() {
  const tenantId = yield select(getUserTenantId)
  const merchantId = yield select(getUserMerchantId)
  const rolesList = yield select(getUserRoles)

  const params = {
    urlParams: {
      tenantId,
      merchantId
    },
    headers: {
      tenantId,
      merchantId,
      roles: userRolesString(rolesList)
    }
  }

  yield put(doLocalLoading({ id: 'userGetTerminalIdList', value: true }))
  yield put(actions.getTerminalIdList.request(params))
  const { error, response } = yield call(api.callGetTerminalIdList, params)
  if (!error) {
    yield put(actions.getTerminalIdList.success(response, response.data))
  } else {
    yield put(actions.getTerminalIdList.failure(response, error))
  }
  yield put(doLocalLoading({ id: 'userGetTerminalIdList', value: false }))
}

export function* getSubscriptionSaga() {
  const tenantId = yield select(getUserTenantId)
  const merchantId = yield select(getUserMerchantId)
  const rolesList = yield select(getUserRoles)

  const headers = deleteEmptyValues({ merchantId, tenantId, roles: userRolesString(rolesList) })

  yield put(actions.retrieveSubscriptionGlobal.request(headers))
  const { error, response } = yield call(api.getSubscriptionGlobal, headers)

  if (!error) {
    yield put(actions.retrieveSubscriptionGlobal.success(response.data))
    const popupData = findPopupData(response?.data?.subscriptions || [])
    
    if (popupData?.vas) {
      yield put(doPopup('MANAGE_VAS_POPUP', { vas: popupData.vas }))
    }
  } else {
    yield put(actions.retrieveSubscriptionGlobal.failure(response, error))
  }
}

export function* postSubscriptionGlobal({ vas, values, payload }) {
  yield put(doLocalLoading({ id: 'manageVas', value: true }))

  const tenantId = yield select(getUserTenantId)
  const terminalId = vas?.terminalId
  const merchantId = yield select(getUserMerchantId)
  const selectedVas = vas
  const roles = yield select(getUserRoles)
  const params = deleteEmptyValues({
    body: {
      status: values?.status,
      featureName: selectedVas?.feature?.name,
      ...payload
    },
    urlParams: {
      tenantId,
      merchantId,
      terminalId,
      subscriptionId: selectedVas?.id
    },
    headers: {
      tenantId,
      merchantId,
      roles: userRolesString(roles)
    }
  })

  yield put(actions.postSubscriptionGlobal.request(params))
  const { error, response } = yield call(api.postSubscriptionGlobal, params)
  if (!error) {
    yield put(actions.postSubscriptionGlobal.success(response, response.data))
    yield put(actions.doGetSubscriptionGlobal())
    yield put(actionTerminal.doFetchVasList(terminalId))
    yield put(doPopup(''))
    yield put(
      doPopup('RESULT_POPUP', {
        ...popupSuccessResult,
        title: vasFormattedMessage.done,
        description: vasFormattedMessage.successSubtitle
      })
    )
  } else {
    yield put(actions.postSubscriptionGlobal.failure(error, response))
    yield put(
      doPopup('RESULT_POPUP', {
        ...popupErrorResult,
        title: vasFormattedMessage.failed
      })
    )
  }
  yield put(doLocalLoading({ id: 'manageVas', value: false }))
}

export function* retrieveUserProfile({ payload }) {
  const merchantId = yield select(getUserMerchantId)
  const tenantId = yield select(getUserTenantId)
  const roleList = yield select(getUserRoles)
  const profileParams = payload?.search !== undefined && payload.selected === undefined
    ? { ...payload, selected: merchantId, tenantId }
    : payload
  const params = payload !== undefined ? deleteEmptyValues(profileParams) : {}
  const headers = deleteEmptyValues({ merchantId, tenantId, roles: userRolesString(roleList) })

  const { search } = payload !== undefined ? payload : { search: '' }
  const idLoading = search !== undefined ? 'searchUserProfile' : 'userProfile'
  yield put(doLocalLoading({ id: idLoading, value: true }))

  yield put(actions.retriveUserProfile.request({ headers, params }))
  const { error, response } = yield call(api.callUserProfile, headers, params)
  if (!error) {
    yield put(actions.retriveUserProfile.success(response?.data))
    const isSSO = yield select(isSSOUser)

    if (response?.data?.merchant?.uiTheme !== getBankFromPath()) {
      const dataPopup = {
        title: messages.themePopupTitle,
        description: messages.themePopupSubtitle,
        primaryButtonLabel: messages.exit,
        primaryAction: {
          type: 'METHOD',
          payload: () => logoutRedirect(isSSO),
          closePopup: false
        },
      }
      yield put(doPopup('GENERIC_POPUP', dataPopup))
      // Timeout of security to allow the user to read the popup
      yield delay(5000)
      yield call(logoutRedirect, isSSO)
    } else {
      if (response?.data?.merchant?.timeZone) window.sessionStorage.setItem('timeZone', response.data.merchant.timeZone)
      if (response?.data?.tenantId) {
        window.sessionStorage.setItem('tenant', response?.data?.tenantId)
        yield put(actionsLang.doSetLanguageProvider({ portal: response?.data?.tenantId }))
        yield call(getSubscriptionSaga) // Call the getsubscription only if the getprofile is success since has the tenantId
        yield put(doLocalLoading({ id: idLoading, value: false }))
      }
    }
  } else {
    yield put(doLocalLoading({ id: idLoading, value: false }))
    yield put(actions.retriveUserProfile.failure(response, error))
  }
}

function* logoutSaga() {
  const isSSO = yield select(isSSOUser)
  yield call(logoutRedirect, isSSO)
}
// @generator saga

// @generator saga:watch

function* watchLogoutSaga() {
  yield takeLatest(actions.actionTypes.DO_LOGOUT, logoutSaga)
}

function* watchGetTerminalIdList() {
  yield takeLatest(actions.actionTypes.DO_FETCH_TERMINAL_ID_LIST, getTerminalIdListSaga)
}

function* watchRetrieveUserProfile() {
  yield takeLatest(actions.actionTypes.DO_GET_USER_PROFILE, retrieveUserProfile)
}

function* watchGetSubscription() {
  yield takeLatest(actions.actionTypes.DO_GET_SUBSCRIPTION_GLOBAL, getSubscriptionSaga)
}

function* watchPostSubscriptionGlobal() {
  while (true) {
    const { payload } = yield take(actions.actionTypes.DO_POST_SUBSCRIPTION_GLOBAL)
    yield call(postSubscriptionGlobal, payload)
  }
}

/**
 * Root saga manages watcher lifecycle
 */
export default function* rootSaga() {
  yield all([
    watchGetTerminalIdList(),
    watchRetrieveUserProfile(),
    watchGetSubscription(),
    watchLogoutSaga(),
    watchPostSubscriptionGlobal()
    // @generator saga:fork
  ])
}
